import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FaEnvelope, FaEye, FaEyeSlash, FaKey, FaLock, FaPhoneAlt, FaUser } from "react-icons/fa";
import Handler from "../Helpers/Handler";
import { textCapitalize } from "../Helpers/Utils";
import { contentLoadingChange } from "../Reducer/componentSlice";
import LoginLeft from "../Assets/images/login-left-card.svg";
import Logo from "../Assets/images/logo.svg";

const validate = ({ signupSecret, name, phone, email, password, confirmPassword }) => {
    if (!signupSecret?.trim()) return "Enter the signup secret";
    if (!name?.trim()) return "Enter the admin's name";
    if (!/^[0-9]{6,20}$/.test(String(phone || "").trim())) return "Enter a valid phone number";
    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(String(email || "").trim())) return "Enter a valid email";
    if (String(password || "").length < 6) return "Password must be at least 6 characters";
    if (password !== confirmPassword) return "Passwords do not match";
    return "";
};

const Register = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

    const register = async () => {
        const error = validate(form);
        if (error) {
            toast.error(error);
            return;
        }

        setBusy(true);
        dispatch(contentLoadingChange(true));
        const response = await Handler({
            method: "post",
            url: "/admin-auth/register",
            data: {
                signupSecret: form.signupSecret.trim(),
                name: form.name.trim(),
                phone: form.phone.trim(),
                email: form.email.trim().toLowerCase(),
                password: form.password,
            },
        });
        if (response?.success) {
            toast.success(response?.data?.message || "Admin account created");
            setTimeout(() => navigate("/", { replace: true }), 1500);
        } else {
            toast.error(textCapitalize(response?.data?.message || "Unable to create the account"));
            dispatch(contentLoadingChange(false));
            setBusy(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        register();
    };

    return (
        <div className="login-page d-flex align-items-center justify-content-center">
            <div className="login-card shadow-lg rounded-4 overflow-hidden">
                <div className="row g-0">
                    <div className="col-lg-6 d-none d-lg-flex login-illustration flex-column align-items-center justify-content-center text-center p-5">
                        <img src={LoginLeft} alt="Secure registration illustration" className="login-illustration-img mb-4" />
                        <h5 className="fw-semibold mb-2">Create an admin account</h5>
                        <p className="text-muted mb-0 px-4">Registration is gated by the signup secret shared with your team.</p>
                    </div>

                    <div className="col-12 col-lg-6 login-form-pane d-flex flex-column justify-content-center p-4 p-sm-5">
                        <div className="d-flex flex-column align-items-center text-center text-lg-start mb-4">
                            <img src={Logo} alt="logo" className="login-logo-img mb-3" />
                            <h4 className="fw-semibold mb-1">Admin registration</h4>
                            <p className="text-muted mb-0">Fill in the details to create a new admin account</p>
                        </div>

                        <div className="mb-3 text-start">
                            <label htmlFor="register-secret" className="form-label">Signup secret</label>
                            <div className="input-group">
                                <span className="input-group-text bg-white"><FaKey className="text-muted" /></span>
                                <input
                                    id="register-secret"
                                    type="password"
                                    name="signupSecret"
                                    autoComplete="off"
                                    autoFocus
                                    placeholder="Shared signup secret"
                                    className="form-control"
                                    disabled={busy}
                                    value={form.signupSecret || ""}
                                    onChange={handleChange}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                        </div>

                        <div className="mb-3 text-start">
                            <label htmlFor="register-name" className="form-label">Name</label>
                            <div className="input-group">
                                <span className="input-group-text bg-white"><FaUser className="text-muted" /></span>
                                <input
                                    id="register-name"
                                    type="text"
                                    name="name"
                                    autoComplete="name"
                                    placeholder="Full name"
                                    className="form-control"
                                    disabled={busy}
                                    value={form.name || ""}
                                    onChange={handleChange}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                        </div>

                        <div className="mb-3 text-start">
                            <label htmlFor="register-phone" className="form-label">Phone</label>
                            <div className="input-group">
                                <span className="input-group-text bg-white"><FaPhoneAlt className="text-muted" /></span>
                                <input
                                    id="register-phone"
                                    type="tel"
                                    name="phone"
                                    inputMode="numeric"
                                    autoComplete="tel"
                                    placeholder="10-digit mobile number"
                                    className="form-control"
                                    disabled={busy}
                                    value={form.phone || ""}
                                    onChange={handleChange}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                            <small className="text-muted">Sign-in OTPs are sent to this number.</small>
                        </div>

                        <div className="mb-3 text-start">
                            <label htmlFor="register-email" className="form-label">Email</label>
                            <div className="input-group">
                                <span className="input-group-text bg-white"><FaEnvelope className="text-muted" /></span>
                                <input
                                    id="register-email"
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    placeholder="you@company.com"
                                    className="form-control"
                                    disabled={busy}
                                    value={form.email || ""}
                                    onChange={handleChange}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                        </div>

                        <div className="mb-3 text-start">
                            <label htmlFor="register-password" className="form-label">Password</label>
                            <div className="input-group">
                                <span className="input-group-text bg-white"><FaLock className="text-muted" /></span>
                                <input
                                    id="register-password"
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    autoComplete="new-password"
                                    placeholder="At least 6 characters"
                                    className="form-control"
                                    disabled={busy}
                                    value={form.password || ""}
                                    onChange={handleChange}
                                    onKeyDown={handleKeyDown}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    tabIndex={-1}
                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                        </div>

                        <div className="mb-4 text-start">
                            <label htmlFor="register-confirm" className="form-label">Confirm password</label>
                            <div className="input-group">
                                <span className="input-group-text bg-white"><FaLock className="text-muted" /></span>
                                <input
                                    id="register-confirm"
                                    type={showPassword ? "text" : "password"}
                                    name="confirmPassword"
                                    autoComplete="new-password"
                                    placeholder="Re-enter the password"
                                    className="form-control"
                                    disabled={busy}
                                    value={form.confirmPassword || ""}
                                    onChange={handleChange}
                                    onKeyDown={handleKeyDown}
                                />
                            </div>
                        </div>

                        <button
                            className="btn btn-primary w-100 py-2 d-flex align-items-center justify-content-center gap-2"
                            disabled={busy}
                            onClick={register}
                        >
                            {busy && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />}
                            {busy ? "Creating account..." : "Create account"}
                        </button>

                        <div className="text-center mt-3">
                            <button type="button" className="btn btn-link btn-sm text-decoration-none" disabled={busy} onClick={() => navigate("/")}>
                                Already have an account? Sign in
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Register;
